import { SiteStateService } from 'app/shared/site_state.service';
import { ZoneCollection } from 'app/shared/collections/zones/zone_collection.service';
import { MapOptionsService } from 'app/shared/map_options/MapOptionsService';
import { ImsObjectVisibilityService } from 'app/shared/visibility/ImsObjectVisibilityService';
import { SelectionService } from 'app/shared/selection/SelectionService';
import { ImsItemTypeDetails, ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import { getSelectedZones } from 'app/shared/selection/selection_utilities';
import { Zone } from 'app/shared/collections/zones/zone';
import { CesiumZone } from 'app/map/geometry/cesium/CesiumZones/CesiumZone';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { CesiumZoneCollection } from 'app/map/geometry/cesium/CesiumZones/CesiumZoneCollection';
import { CesiumZonePropertyUpdater } from 'app/map/geometry/cesium/CesiumZones/CesiumZonePropertyUpdater';
import { STAT_DEFINITION__PARENT_TYPE_ZONE } from 'app/shared/collections/StatDefinition';

export class CesiumZoneUpdater {
    private _zoneCollection: ZoneCollection;
    private _clockHandler: ClockHandler;
    private _cesiumZoneCollection: CesiumZoneCollection;
    private _cesiumZonePropertyUpdater: CesiumZonePropertyUpdater;
    private _selectionService: SelectionService;
    private _zoneOptionsService: MapOptionsService;
    private _imsObjectVisibilityService: ImsObjectVisibilityService;
    private _imsItemTypeRegistry: ImsItemTypeRegistry;

    constructor(
        zoneCollection: ZoneCollection,
        cesiumZoneCollection: CesiumZoneCollection,
        siteStateService: SiteStateService,
        zoneOptionsService: MapOptionsService,
        imsObjectVisibilityService: ImsObjectVisibilityService,
        selectionService: SelectionService,
        imsItemTypeRegistry: ImsItemTypeRegistry,
        clockHandler: ClockHandler,
        cesiumZonePropertyUpdater: CesiumZonePropertyUpdater,
        shouldInitialise: boolean,
    ) {
        this._zoneOptionsService = zoneOptionsService;
        this._cesiumZoneCollection = cesiumZoneCollection;
        this._cesiumZonePropertyUpdater = cesiumZonePropertyUpdater;
        this._selectionService = selectionService;
        this._clockHandler = clockHandler;
        this._zoneCollection = zoneCollection;
        this._imsItemTypeRegistry = imsItemTypeRegistry;
        this._zoneOptionsService = zoneOptionsService;
        this._imsObjectVisibilityService = imsObjectVisibilityService;
        this._imsItemTypeRegistry = imsItemTypeRegistry;

        if (shouldInitialise) {
            this._initListeners();
        }
    }

    private _initListeners() {
        // Handle change in zone map options
        this._zoneOptionsService.addOnMapOptionsChanged((changedMapOptions) => {
            this._recalculateAllZoneValues();
            this._applyPendingRefreshToAllZones();
        });

        this._imsObjectVisibilityService.subscribeToZoneChanges(() => {
            this._updateShowForAllZones();

            // We apply the changes to all zones straight away so the user has instant feedback
            this._applyPendingRefreshToAllZones();
        });

        // Handle selections - currently just updating all the things as it's very fast since it is only for
        // the newly selected zone
        this._selectionService.onSelectionChange(({ selected, deselected }) => {
            getSelectedZones(selected, this._zoneCollection).forEach((zone) => {
                this.fullZoneUpdate(zone);
            });
            getSelectedZones(deselected, this._zoneCollection).forEach((zone) => {
                this.fullZoneUpdate(zone);
            });
        });

        // Change on stats loading
        this._imsItemTypeRegistry.addOnImsItemTypeRegistryReady(() => {
            const statNames = this._imsItemTypeRegistry.getRegisteredStatTypes();

            // only listen to zone stats
            const zoneItemTypeDetails: Array<ImsItemTypeDetails> = statNames
                .map((statName) => this._imsItemTypeRegistry.getTypeDetails(statName))
                .filter(
                    // TODO this could be a tasty optional chain, once we upgrade prettier
                    (typeDetail) =>
                        typeDetail.statDefinition &&
                        typeDetail.statDefinition.parent_type &&
                        typeDetail.statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_ZONE,
                );
            zoneItemTypeDetails.forEach((filteredDetail) => {
                filteredDetail.collection.addOnChangedListener(() =>
                    this._recalculateAllZoneColorAndLabel(),
                );
                filteredDetail.collection.addOnIntervalsChangedListener(() =>
                    this._recalculateAllZoneColorAndLabel(),
                );
            });
        });
    }

    /**
     * Recalculates label and color for all zones, should not be used often
     * @private
     */
    private _recalculateAllZoneValues() {
        for (const zoneKey in this._zoneCollection.all) {
            const zone = this._zoneCollection.all[zoneKey];
            this.fullZoneUpdate(zone);
        }
    }

    /**
     * Recalculates all values for all zones, should not be used often
     * @private
     */
    private _recalculateAllZoneColorAndLabel() {
        for (const zoneKey in this._zoneCollection.all) {
            const zone = this._zoneCollection.all[zoneKey];
            this._zoneLabelAndColorUpdate(zone);
        }
    }

    protected _zoneLabelAndColorUpdate(imsZone: Zone) {
        const time = this._clockHandler.getCursorTimeAsJulian();
        const cesiumZone: CesiumZone = this._cesiumZoneCollection.get(imsZone.id);
        if (!cesiumZone) {
            return;
        }

        const editing = imsZone.editingPoints || false;

        cesiumZone.selected =
            this._selectionService.isSelected(imsZone.id, SelectionType.ZONE) || false;

        this._cesiumZonePropertyUpdater.updateZoneLabel(imsZone, cesiumZone, time);
        this._cesiumZonePropertyUpdater.updateZoneColor(imsZone, cesiumZone, time);

        cesiumZone.editingPoints = editing;

        // Recalculates and sets show for the zones
        this._cesiumZonePropertyUpdater.updateShowForZone(imsZone, cesiumZone, time);

        // Applies the changes
        cesiumZone.applyPendingRefreshes();
    }

    /**
     * Creates an id to be used for the zone shadow's Cesium Entity
     */
    public fullZoneUpdate(imsZone: Zone) {
        const time = this._clockHandler.getCursorTimeAsJulian();
        const cesiumZone: CesiumZone = this._cesiumZoneCollection.get(imsZone.id);
        if (!cesiumZone) {
            return;
        }

        const editing = imsZone.editingPoints || false;

        cesiumZone.selected =
            this._selectionService.isSelected(imsZone.id, SelectionType.ZONE) || false;

        this._cesiumZonePropertyUpdater.updateZonePolygonPositions(imsZone, cesiumZone, time);
        this._cesiumZonePropertyUpdater.updateZoneLabel(imsZone, cesiumZone, time);
        this._cesiumZonePropertyUpdater.updateZoneColor(imsZone, cesiumZone, time);

        cesiumZone.editingPoints = editing;

        // Recalculates and sets show for the zones
        this._cesiumZonePropertyUpdater.updateShowForZone(imsZone, cesiumZone, time);

        // Applies the changes
        cesiumZone.applyPendingRefreshes();
    }

    /**
     * Zones changes are in most cases not applied when they are made to a zone for performance reasons.
     * This actually applies those changes.
     */
    private _applyPendingRefreshToAllZones() {
        for (const zoneKey in this._zoneCollection.all) {
            const cesiumZone = this._cesiumZoneCollection.zones[zoneKey];
            if (!cesiumZone) {
                continue;
            }
            cesiumZone.applyPendingRefreshes();
        }
    }

    /**
     * Updates the show state for both the polygon and label for all zones
     */
    private _updateShowForAllZones() {
        const theTime = this._clockHandler.getCursorTimeAsJulian();

        for (const cesiumZone of this._cesiumZoneCollection.getAllAsArray()) {
            const imsZone = this._zoneCollection.get(cesiumZone.id);
            if (!cesiumZone || !imsZone) {
                continue;
            }
            this._cesiumZonePropertyUpdater.updateShowForZone(imsZone, cesiumZone, theTime);
        }
    }
}
